/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  useMemo,
  Fragment as _,
  cloneElement,
  RefObject,
} from 'react';
import certificateDefaultPNG from 'assets/Certificate-V2.png';
import chameleonLogoForCertificate from 'assets/chameleonLogoForCertificate.svg';
import { interactionContext as InteractionContext } from './interactionContext';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import getBlockById from 'services/apiHelpers/getBlockById';
import unlockInteraction from 'services/unlockInteraction';
import { DragHandle, ResizeHandle } from './Handles';
import { generatePDFFromCanvas } from './generatePDF';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import { CertificateTemplate } from './CertificateTemplates';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import { InputText } from './InputText';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';

interface ICertificateContext {
  certificateRef: RefObject<HTMLDivElement> | null;
  certificateImgSize: { width: number; height: number } | undefined;
  scaleFactor: number;
  inverseScaleFactor: number;
  generatingPDF: boolean;
}

const certificateDefaultSize = {
  width: 2084,
  height: 1437,
};

export const certificatePadding = 20;

const certificateContextValue = {
  certificateRef: null,
  certificateImgSize: {
    ...certificateDefaultSize,
  },
  scaleFactor: 1,
  inverseScaleFactor: 1,
  generatingPDF: false,
};

export const CertificateContext = createContext<ICertificateContext>(
  certificateContextValue
);

const resetResponsiveStyles = {
  p: {
    fontSize: '1rem !important',
  },
  h1: {
    fontSize: '3.5rem !important',
  },

  h2: {
    fontSize: '2.5rem !important',
  },

  h3: {
    fontSize: '2rem !important',
  },

  h4: {
    fontSize: '1.7rem !important',
  },

  h5: {
    fontSize: '1.2rem !important',
  },

  h6: {
    fontSize: '1rem !important',
  },
};

const Certificate = props => {
  const interactionContext = useContext(InteractionContext);

  const {
    lockDown,
    name,
    certificateType = 'custom',
  } = interactionContext.props;

  const {
    id,
    imgWidth = certificateDefaultSize.width,
    imgHeight = certificateDefaultSize.height,
    imageId: imgId = undefined,
    children,
  } = props;

  const [textFields, downloadBtn] = children;

  //
  let imgSrc = useCachedResource(CHAMELEON_CACHE_v1, props.src) || props?.src;

  // if we don't have an imgSrc at this point we default to the appropriate placeholder. . .
  if (!imgSrc) {
    imgSrc =
      certificateType === 'custom'
        ? certificateDefaultPNG
        : chameleonLogoForCertificate;
  }
  //

  const [generatingPDF, setGeneratingPDF] = useState(false);

  const imgSize = {
    width:
      certificateType === 'custom' ? (imgWidth > 1100 ? imgWidth : 1100) : 2084,
    height: certificateType === 'custom' ? imgHeight : 1471.6,
  };

  const isOpen = useSelector((state: State) => {
    return state.interactionsOpen.includes(interactionContext.id);
  });

  const [scaleFactor, setScaleFactor] = useState(1);
  const inverseScaleFactor = 1 / scaleFactor;

  const certificateId = `certificate-${id}`;
  const certificate = useRef<HTMLDivElement>(null);

  const imgSrcToArray = props.src?.split(/\./g);
  const imgType = imgSrcToArray?.[imgSrcToArray.length - 1] as
    | 'svg'
    | 'png'
    | 'PNG'
    | 'jpg'
    | 'JPG'
    | 'jpeg'
    | 'JPEG';

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();

    function onResize() {
      const certificateWidth = certificate.current?.getBoundingClientRect()
        .width;

      if (!(certificateWidth && imgSize?.width)) {
        return;
      }

      let calculatedScaleFactor = certificateWidth / imgSize?.width;

      if (['svg', 'SVG'].includes(imgType)) {
        // we're gonna have to do some dancin' queen to support svg
      }

      setScaleFactor(calculatedScaleFactor);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [imgSrc, imgId, imgWidth, imgHeight, certificateType, isOpen]);

  return (
    <CertificateContext.Provider
      value={
        {
          ...CertificateContext,
          certificateRef: certificate,
          certificateImgSize: imgSize,
          scaleFactor,
          inverseScaleFactor,
          generatingPDF,
        } as ICertificateContext
      }
    >
      <div style={{ padding: certificatePadding }}>
        {certificateType === 'custom' ? (
          <_>
            <div
              id={`${certificateId}-${certificateType}`}
              style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={imgSrc}
                style={{
                  ...(imgType === 'svg' &&
                    {
                      //
                    }),

                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
                // data-html2canvas-ignore
              />

              <div
                ref={certificate}
                style={{
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  userSelect: 'none',
                  pointerEvents: 'none',

                  ...(!window.dev() && {
                    opacity: imgSize ? 1 : 0,
                    transition: 'opacity 0.2s',
                  }),
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      transform: `scale(${scaleFactor})`,
                      transformOrigin: '0px 0px',
                      pointerEvents: 'auto',
                    }}
                  >
                    {textFields}
                  </div>
                </div>
              </div>
            </div>
          </_>
        ) : (
          <div ref={certificate} id={`${certificateId}-${certificateType}`}>
            <CertificateTemplate
              {...{
                ...props,
                src: imgSrc,
              }}
            />
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '50px 0 0 0',
          pointerEvents: 'auto',
        }}
        data-html2canvas-ignore
      >
        {cloneElement(downloadBtn, {
          onClick() {
            setGeneratingPDF(true);

            generatePDFFromCanvas({
              imgSize,
              scale: certificateType === 'custom' ? inverseScaleFactor : 2,
              id: `${certificateId}-${certificateType}`,
              fileName: name,
              imgSrc,
              imgType,
              certificateType,
              onComplete() {
                setGeneratingPDF(false);
              },
            });

            if (lockDown === 0) {
              return;
            }

            unlockInteraction(interactionContext.id);
          },
        })}
      </div>

      {generatingPDF && (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            background: 'rgba(255,255, 255, 0.1)',
            pointerEvents: 'none',
          }}
        >
          <LoadingSpinner />
        </div>
      )}
    </CertificateContext.Provider>
  );
};

const CertificateTextFields = props => {
  const { children } = props;

  return (
    <div
      css={css({
        fontSize: 16 * 10,
      })}
    >
      {children}
    </div>
  );
};

const CertificateTextField = props => {
  const certificateContext = useContext(CertificateContext);

  const {
    certificateRef,
    certificateImgSize,
    scaleFactor = 1,
    inverseScaleFactor = 1,
  } = certificateContext as ICertificateContext;

  const { id, coords, isInputField, children } = props;

  let [txt] = children;

  let txtProps;
  let content;

  const txtDoc = useSelector((state: State) => {
    txtProps = state.blocks[txt.props.id]?.props || {};
    content = txtProps?.content || '';

    return new DOMParser().parseFromString(content, 'text/html');
  });

  const textAlign = content?.includes('chameleon-text-align-center')
    ? 'center'
    : content?.includes('chameleon-text-align-right')
    ? 'right'
    : 'left';

  const spans = Array.from(txtDoc.querySelectorAll('span'));
  const strong = Array.from(txtDoc.querySelectorAll('strong'));
  const em = Array.from(txtDoc.querySelectorAll('em'));
  const u = Array.from(txtDoc.querySelectorAll('u'));

  const fontSpan = spans.filter(span => {
    return span.getAttribute('data-type') === 'fontSize';
  })[0];

  const fontData = spans.reduce(
    (acc, curr) => {
      const fontSize = curr.style['font-size'];
      const fontFamily = curr.style['font-family'];
      const color = curr.style['color'];

      return {
        ...acc,
        ...(fontSize && {
          fontSize,
        }),
        ...(fontFamily && {
          fontFamily: fontFamily.replace(/"/g, ''),
        }),
        ...(color && {
          color,
        }),
      };
    },
    {
      bold: !!strong?.length,
      italic: !!em?.length,
      underline: !!u?.length,
    }
  );

  let {
    fontSize,
    fontFamily,
    color,
    bold,
    italic,
    underline,
  } = fontData as any;

  if (fontSize) {
    fontSize = Number(fontSize.replace(/rem|px/g, '')) || 1;
  } else {
    fontSize = 1;
  }

  let initializeFontSizeScaleFactor =
    certificateImgSize?.width && certificateImgSize.width < 1100
      ? 1
      : ((certificateImgSize && certificateImgSize?.width) ||
          certificateDefaultSize?.width) / 1100;

  const [localCoords, setLocalCoords] = useState(coords || {});

  let { x, y, width, height } = localCoords;

  if (!window.dev() && isInputField) {
    y = `${parseInt(y, 10) + 5}px`;
  }

  const block = useMemo(() => {
    return getBlockById(id);
  }, []);

  const delaySetBlockProps = useRef<ReturnType<typeof setTimeout>>();
  const certificateTextField = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!Object.keys(localCoords).length) {
      return;
    }

    delaySetBlockProps.current && clearTimeout(delaySetBlockProps.current);

    delaySetBlockProps.current = setTimeout(() => {
      setBlockProps(block, {
        coords: localCoords,
      });
    }, 300);
  }, [x, y, width, height]);

  const defaultWidth = 400;

  const defaultHeight =
    certificateImgSize?.width && certificateImgSize.width < 1100 ? 35 : 75;
  const defaultLeft =
    (certificateImgSize?.width || 1100) / 2 - defaultWidth / 2;
  const defaultTop = (certificateImgSize?.height || 0) / 2 - defaultHeight / 2;

  let paragraphMaxWidth = defaultWidth;

  if (width && typeof width === 'string') {
    paragraphMaxWidth = Number(width.replace('px', ''));
  }

  return (
    <div
      ref={certificateTextField}
      id={`chameleon-certificate-text-${id}`}
      className="chameleon-certificate-text fader_-2s"
      css={css({
        position: 'absolute',
        left: x || defaultLeft,
        top: y || defaultTop,
        width: width || defaultWidth,
        height: height || defaultHeight,
        outline: window.dev() ? '1px solid lightGrey' : 'none',

        overflow: 'hidden',

        ...resetResponsiveStyles,
        '@media(min-width: 480px)': {
          ...resetResponsiveStyles,
        },
        '@media(min-width: 767px)': {
          ...resetResponsiveStyles,
        },

        '[class*="EditableText"]': {
          height: '100%',
          padding: '15px 10px',
        },

        '[class*="ReadOnlyText"]': {
          height: '100%',
          padding: '15px 10px',

          'h1, h2, h3, h4, h5, h6': {
            lineHeight: 'unset',
          },
        },

        '.chameleon-edit-marker': {
          display: 'none',
          ...(inverseScaleFactor > 1 && {
            transform: `scale(${inverseScaleFactor})`,
            transformOrigin: 'top right',
          }),
        },

        '.chameleon-text div p': {
          fontSize: `${fontSize * initializeFontSizeScaleFactor}rem !important`,
        },
      })}
    >
      {window.dev() ? (
        cloneElement(txt, { certificate: true })
      ) : !isInputField ? (
        cloneElement(txt, { certificate: true })
      ) : (
        <div
          css={css({
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',

            '*': {
              padding: '0 0 0 10px',
              fontSize: `${fontSize *
                initializeFontSizeScaleFactor}rem !important`,
              fontFamily,
              fontStyle: italic ? 'italic' : 'normal',
              fontWeight: bold ? 700 : 'normal',
              textDecoration: underline ? 'underline' : 'none',
              color,
              '.data-input-blur-value-display': {
                padding: '0 0 0 0px',
              },

              textAlign,
            },

            input: {
              padding: '0 0 0 0px',

              ':focus': {
                padding: '0 0 0 10px',
              },
            },
          })}
        >
          <InputText
            {...{
              width: '100%',
              height,
              placeholder: txtDoc.body.innerText,
              textAlign,
            }}
          />
        </div>
      )}

      {window.dev() && (
        <_>
          <DragHandle
            key={`DRAG_HANDLE_${scaleFactor + inverseScaleFactor}`}
            id={id}
            elementRef={certificateTextField}
            scaleFactor={scaleFactor}
            inverseScaleFactor={inverseScaleFactor}
            onDragEnd={() => {
              if (!certificateTextField?.current) {
                return;
              }

              const { left, top } = getComputedStyle(
                certificateTextField?.current
              );

              const parsedLeft = parseInt(left, 10);
              const parsedTop = parseInt(top, 10);

              setLocalCoords(localCoords => ({
                ...localCoords,
                x: `${parsedLeft}px`,
                y: `${parsedTop}px`,
              }));
            }}
            isInput={isInputField}
          />

          <ResizeHandle
            key={`RESIZE_HANDLE_${scaleFactor + inverseScaleFactor}`}
            id={id}
            scaleFactor={scaleFactor}
            inverseScaleFactor={inverseScaleFactor}
            onDrag={data => {
              //
              setLocalCoords(localCoords => ({
                ...localCoords,
                width: data.left,
                height: data.top,
              }));
            }}
            isInput={isInputField}
          />
        </_>
      )}
    </div>
  );
};

export default Certificate;
export { Certificate, CertificateTextFields, CertificateTextField };
